import type { NormalizedCacheObject } from '@apollo/client'
import { ApolloClient, InMemoryCache, createHttpLink, ApolloLink } from '@apollo/client'
import { HttpLink } from '@apollo/client/link/http'
import { RetryLink } from '@apollo/client/link/retry'
import { setContext } from '@apollo/client/link/context'
import cache from './cache'
import { getGraphQLErrors } from './errors'
import { Auth } from 'aws-amplify'
import type { GraphQLFormattedError } from 'graphql'

import type { IncomingHttpHeaders } from 'http'
import { getAccountIdForPricing } from '../customerPricing/customerPricingStore'

const graphqlUri =
  typeof window === 'undefined' ? process.env.NEXT_PUBLIC_GRAPHQL_URL : '/api/graphql'

// Retry on gateway timeout
const retry504 = new RetryLink({
  attempts: (count, _op, error) => {
    const status = error?.statusCode || error?.response?.status
    return count <= 2 && status === 504
  },
  delay: (count) => Math.min(1500 * count, 3000),
})

const httpLink = new HttpLink({
  uri: graphqlUri,
  credentials: 'include',
})

export const isUnauthenticatedGraphQLError = (error: GraphQLFormattedError) =>
  error.extensions?.code === 'UNAUTHENTICATED'

export const isUnauthenticatedApolloError = (error: unknown) =>
  getGraphQLErrors(error).some(
    (graphQLError) => graphQLError.extensions?.code === 'UNAUTHENTICATED',
  )

export const createAuthLink = () =>
  setContext(async (_, { headers }) => {
    let token = ''
    if (typeof window !== 'undefined') {
      const { state } = JSON.parse(localStorage.getItem('state') || '{}')
      token = state?.authState?.token || state?.vendorAuthState?.token || ''
    }

    const accountIdForPricing = typeof window === 'undefined' ? undefined : getAccountIdForPricing()

    try {
      await Auth.currentSession()
    } catch (_) {
      // ignored: auth session can be absent for guest flows
    }

    return {
      headers: {
        ...headers,
        authorization: `Bearer ${token}`,
        ...(accountIdForPricing ? { 'x-admin-account-id-for-pricing': accountIdForPricing } : {}),
      },
    }
  })

const sharedAuthLink = createAuthLink()

const authLink = setContext((_, { headers }) => {
  return {
    headers: {
      ...headers,
      'x-api-key':
        typeof window === 'undefined'
          ? undefined
          : new URLSearchParams(window.location.search).get('apiKey') || undefined,
    },
  }
})

const createApolloClient = () =>
  new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: ApolloLink.from(
      typeof window === 'undefined' ? [httpLink] : [sharedAuthLink, authLink, retry504, httpLink],
    ),
    cache: new InMemoryCache(cache),
    clientAwareness: {
      name: 'fusion',
      version: process.env.NEXT_PUBLIC_COMMIT_SHA || 'localhost',
    },
  })

let apolloClient: ApolloClient<NormalizedCacheObject>

const createApolloClientSsr = (ssrHeaders?: IncomingHttpHeaders) => {
  const isServer = typeof window === 'undefined'

  const ssrHttpLink = new HttpLink({
    uri: process.env.NEXT_PUBLIC_GRAPHQL_URL, // SSR hits router directly
    credentials: 'include',
    headers: {
      cookie: ssrHeaders?.cookie ?? '',
      ...(ssrHeaders?.authorization ? { authorization: ssrHeaders.authorization } : {}),
    },
  })

  return new ApolloClient({
    ssrMode: isServer,
    link: ApolloLink.from(
      isServer ? [retry504, ssrHttpLink] : [sharedAuthLink, authLink, retry504, httpLink],
    ),
    cache: new InMemoryCache(cache),
    clientAwareness: {
      name: 'fusion',
      version: process.env.NEXT_PUBLIC_COMMIT_SHA || 'localhost',
    },
  })
}

export const apolloInit = () => {
  const _apolloClient = apolloClient ?? createApolloClient()
  if (typeof window === 'undefined') return _apolloClient
  if (!apolloClient) apolloClient = _apolloClient
  return _apolloClient
}

/**
 * When performing a server side request, request headers are not forwarded on. This means
 * backend auth will fail which could lead to undesirable results. This function adds the request
 * headers.
 * @param ssrHeaders headers you want to pass to the backend e.g. cookie, authorization
 * @returns
 */
export const apolloInitSsr = (ssrHeaders?: IncomingHttpHeaders) => {
  // Always return new, ensuring request headers are used and not re-using
  // headers from another request.
  if (typeof window === 'undefined') return createApolloClientSsr(ssrHeaders)
  return apolloInit()
}
